import React, { useMemo, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { Container, Section, Stack, Layout } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { Button } from '@fiverr-private/button';
import { ChevronDownIcon, TimesIcon } from '@fiverr-private/visuals';
import { appTranslate, departmentLabel } from '../../i18n';
import { SECTION_IDS } from '../../../consts';
import { Job } from '../../../lib/comeet';
import { FilterValues, filterJobs } from '../../../lib/filters';
import { writeFilterParams } from '../../../lib/url';
import { useCvMatch } from '../../lib/cvMatch';
import { useBI, useImpression, SECTION_NAMES } from '../../bi';
import { OpenPositionCard } from '../OpenPositionCard';
import { FilterDropdown } from './OpenPositionsSection.FilterDropdown';
import { UploadCv } from './OpenPositionsSection.UploadCv';
import { heading as headingClass } from './OpenPositionsSection.styles.ve.css';

gsap.registerPlugin(useGSAP, ScrollTrigger);

const PAGE_SIZE = 4;

interface OpenPositionsSectionProps {
    comeetPositions?: Job[];
    initialFilters?: FilterValues;
}

interface FilterState {
    location: string | null;
    department: string | null;
    openedFilter: 'location' | 'department' | null;
}

const CvChip = ({ fileName, onRemove }: { fileName: string; onRemove: () => void }) => (
    <Stack
        as="span"
        direction="row"
        alignItems="center"
        gap="3"
        alignSelf="flexStart"
        paddingX="4"
        paddingY="2"
        backgroundColor="grey_300"
        borderRadius="circle"
        borderStyle="solid"
        borderWidth="sm"
        borderColor="border"
    >
        <Typography
            as="span"
            color="black"
            size="b_lg"
            fontWeight="semibold"
            letterSpacing="-0.18px"
            whiteSpace="nowrap"
        >
            {fileName}
        </Typography>
        <Stack
            as="span"
            role="button"
            tabIndex={0}
            onClick={onRemove}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onRemove()}
            cursor="pointer"
            aria-label={appTranslate('cv_match.remove_file') as string}
        >
            <TimesIcon size="sm" color="black" />
        </Stack>
    </Stack>
);

// Fluid heading for match/nomatch — idle uses the full-bleed CSS class instead.
const ResultHeading = ({ children }: { children: React.ReactNode }) => (
    <Typography
        as="h2"
        color="black"
        fontWeight="light"
        letterSpacing="-0.06em"
        style={{ fontSize: 'clamp(28px, 4vw, 48px)', lineHeight: 1.2 }}
    >
        {children}
    </Typography>
);

export const OpenPositionsSection = ({ comeetPositions = [], initialFilters }: OpenPositionsSectionProps) => {
    const listRef = useRef<HTMLDivElement>(null);
    const prevCountRef = useRef(0);
    const isFirstRevealRef = useRef(true);
    const bi = useBI();
    const impressionRef = useImpression<HTMLDivElement>(() =>
        bi.reportSectionImpression({ sectionName: SECTION_NAMES.openPositions })
    );
    const { hasResult, isMatch, matchedJobs, fileName, reset } = useCvMatch();
    const [filters, setFilters] = useState<FilterState>(() => ({
        location: initialFilters?.location ?? null,
        department: initialFilters?.department ?? null,
        openedFilter: null,
    }));

    const locations = useMemo(() => new Set(comeetPositions.map((j) => j.location)), [comeetPositions]);
    const departments = useMemo(() => new Set(comeetPositions.map((j) => j.department)), [comeetPositions]);

    const filteredJobs = useMemo(() => filterJobs(comeetPositions, filters), [comeetPositions, filters]);

    const [visibleJobs, setVisibleJobs] = useState<Job[]>(() => filteredJobs.slice(0, PAGE_SIZE));

    // 'match' shows the matched subset (no filters/paging); 'nomatch' and 'idle' use the browsable list.
    let mode: 'idle' | 'match' | 'nomatch' = 'idle';
    if (hasResult) {
        mode = isMatch ? 'match' : 'nomatch';
    }
    const displayedJobs = mode === 'match' ? matchedJobs : visibleJobs;
    const hasMore = mode !== 'match' && visibleJobs.length < filteredJobs.length;

    const handleFilterChange = (key: 'location' | 'department', value: string | null) => {
        const newFilters = { ...filters, [key]: value };
        const newFiltered = filterJobs(comeetPositions, newFilters);
        prevCountRef.current = 0;
        setFilters((prev) => ({ ...prev, [key]: value, openedFilter: null }));
        setVisibleJobs(newFiltered.slice(0, PAGE_SIZE));
        writeFilterParams({ location: newFilters.location, department: newFilters.department });
        bi.reportFilterClick({ filterType: key, filterValue: value ?? null });
    };

    const handleToggleFilter = (key: 'location' | 'department') => {
        setFilters((prev) => ({ ...prev, openedFilter: prev.openedFilter === key ? null : key }));
    };

    const handleSeeMore = () => {
        const nextBatch = filteredJobs.slice(visibleJobs.length, visibleJobs.length + PAGE_SIZE);
        setVisibleJobs((prev) => [...prev, ...nextBatch]);
        bi.reportCtaClick({ componentName: 'load_more_positions' });
    };

    useGSAP(
        () => {
            const cards = listRef.current ? Array.from(listRef.current.children) : [];
            const newCards = cards.slice(prevCountRef.current);
            prevCountRef.current = cards.length;
            if (!newCards.length) {
                return;
            }
            const isFirstReveal = isFirstRevealRef.current;
            isFirstRevealRef.current = false;
            gsap.from(newCards, {
                opacity: 0,
                y: 24,
                duration: 0.5,
                stagger: 0.08,
                ease: 'power2.out',
                // Scroll-reveal only the initial batch; See-more / filter / match results reveal immediately.
                ...(isFirstReveal ? { scrollTrigger: { trigger: newCards[0], start: 'top 80%' } } : {}),
            });
        },
        { scope: listRef, dependencies: [displayedJobs.length, mode] }
    );

    return (
        <Container id={SECTION_IDS.openPositions} ref={impressionRef}>
            <Section fullWidth backgroundColor="white">
                <Stack
                    direction="column"
                    gap="16"
                    paddingX={{ default: '5', md: '14' }}
                    paddingY={{ default: '8', md: '24' }}
                >
                    {/* Heading — idle: giant uppercase title; match/no-match: result heading + CV chip */}
                    {mode === 'idle' && (
                        <Typography as="h2" className={headingClass}>
                            {appTranslate('open_positions.heading')}
                        </Typography>
                    )}

                    {mode === 'match' && (
                        <Stack direction="column" gap="6">
                            <ResultHeading>{appTranslate('cv_match.match_heading')}</ResultHeading>
                            {fileName && <CvChip fileName={fileName} onRemove={reset} />}
                        </Stack>
                    )}

                    {mode === 'nomatch' && (
                        <Stack direction="column" gap="6">
                            <ResultHeading>
                                {appTranslate('cv_match.nomatch_heading_1')}
                                <br />
                                {appTranslate('cv_match.nomatch_heading_2')}
                                <Typography
                                    as="a"
                                    href={`#${SECTION_IDS.apply}`}
                                    color="black"
                                    fontWeight="normal"
                                    decoration="underline"
                                >
                                    {appTranslate('cv_match.unusual_link')}
                                </Typography>
                            </ResultHeading>
                            {fileName && <CvChip fileName={fileName} onRemove={reset} />}
                        </Stack>
                    )}

                    {/* Toolbar: idle → upload + filters; nomatch → filters only (right-aligned); match → hidden */}
                    {mode !== 'match' && (
                        <Stack
                            direction={{ default: 'column', md: 'row' }}
                            justifyContent={{ md: mode === 'idle' ? 'spaceBetween' : 'flexEnd' }}
                            alignItems={{ default: 'stretch', md: 'flexEnd' }}
                            gap={{ default: '8', md: '3' }}
                        >
                            {mode === 'idle' && <UploadCv />}
                            <Stack
                                direction={{ default: 'column', md: 'row' }}
                                alignItems={{ default: 'stretch', md: 'center' }}
                                gap="4"
                            >
                                <Typography
                                    as="span"
                                    color="black"
                                    fontWeight="light"
                                    size={{ default: 'b_xl', lg: 'h_lg' }}
                                    letterSpacing="-0.06em"
                                    whiteSpace="nowrap"
                                >
                                    {appTranslate('open_positions.filter_by')}
                                </Typography>
                                <FilterDropdown
                                    label={appTranslate('open_positions.location') as string}
                                    value={filters.location}
                                    options={Array.from(locations).map((loc) => ({ value: loc, label: loc }))}
                                    isOpen={filters.openedFilter === 'location'}
                                    onToggle={() => handleToggleFilter('location')}
                                    onChange={(val) => handleFilterChange('location', val)}
                                />
                                <FilterDropdown
                                    label={appTranslate('open_positions.team') as string}
                                    value={filters.department}
                                    options={Array.from(departments).map((d) => ({
                                        value: d,
                                        label: departmentLabel(d),
                                    }))}
                                    isOpen={filters.openedFilter === 'department'}
                                    onToggle={() => handleToggleFilter('department')}
                                    onChange={(val) => handleFilterChange('department', val)}
                                />
                            </Stack>
                        </Stack>
                    )}

                    {/* List */}
                    <Layout position="relative" width="100%">
                        <Stack ref={listRef} direction="column" gap="6" width="100%">
                            {displayedJobs.map((job) => (
                                <OpenPositionCard key={job.uid} {...job} />
                            ))}
                        </Stack>
                        {hasMore && (
                            <Layout
                                position="absolute"
                                width="100%"
                                height="150px"
                                bottom={0}
                                left={0}
                                background="linear-gradient(to bottom, transparent, #fff)"
                                pointerEvents="none"
                            />
                        )}
                    </Layout>

                    {/* See more — hidden in match mode */}
                    {hasMore && (
                        <Stack justifyContent="center" width="100%">
                            <Button variant="ghost" size="lg" onClick={handleSeeMore}>
                                <Stack as="span" direction="row" gap="4" alignItems="center" justifyContent="center">
                                    {appTranslate('open_positions.see_more')}
                                    <ChevronDownIcon size="sm" />
                                </Stack>
                            </Button>
                        </Stack>
                    )}
                </Stack>
            </Section>
        </Container>
    );
};
